import { useQuery } from '@tanstack/react-query';
import { clerkOpsService } from '../services/clerkOpsService';

/** Gate probe — true only for the Faxter-Clerk tenant. Used to hide the nav
 * item and route for every other store. Cached long; the flag rarely changes. */
export function useClerkOpsEnabled() {
  return useQuery({
    queryKey: ['clerk-ops-enabled'],
    queryFn: () => clerkOpsService.isEnabled(),
    staleTime: 5 * 60_000,
    retry: false, // a 404 means "off", not a transient failure
  });
}

export function useOpsClients() {
  return useQuery({
    queryKey: ['clerk-ops-clients'],
    queryFn: () => clerkOpsService.getClients(),
    refetchInterval: 60_000,
  });
}

export function useOnboardingMetrics() {
  return useQuery({
    queryKey: ['clerk-ops-onboarding'],
    queryFn: () => clerkOpsService.getOnboardingMetrics(),
    refetchInterval: 60_000,
  });
}

export function useOperationsMetrics(period = 'this_month') {
  return useQuery({
    queryKey: ['clerk-ops-operations', period],
    queryFn: () => clerkOpsService.getOperationsMetrics(period),
    refetchInterval: 60_000,
  });
}

export function useOutstandingDocuments() {
  return useQuery({
    queryKey: ['clerk-ops-documents'],
    queryFn: () => clerkOpsService.getOutstandingDocuments(),
  });
}

export function useLeads(days = 30, limit = 100) {
  return useQuery({
    queryKey: ['clerk-ops-leads', days, limit],
    queryFn: () => clerkOpsService.getLeads(days, limit),
    refetchInterval: 60_000,
    retry: false, // endpoint may not be shipped yet upstream
  });
}

export function useSubscriptionPayments(status = 'pending') {
  return useQuery({
    queryKey: ['clerk-ops-subscription-payments', status],
    queryFn: () => clerkOpsService.getSubscriptionPayments(status),
    refetchInterval: 60_000,
    retry: false, // endpoint may not be shipped yet upstream
  });
}

export function useComplianceDeadlines(withinDays = 30) {
  return useQuery({
    queryKey: ['clerk-ops-deadlines', withinDays],
    queryFn: () => clerkOpsService.getComplianceDeadlines(withinDays),
  });
}

export function useClientBilling(clientId: string | null) {
  return useQuery({
    queryKey: ['clerk-ops-client-billing', clientId],
    queryFn: () => clerkOpsService.getClientBilling(clientId!),
    enabled: !!clientId,
    retry: false, // endpoint may not be shipped yet upstream
  });
}

export function useClientActivity(clientId: string | null) {
  return useQuery({
    queryKey: ['clerk-ops-client-activity', clientId],
    queryFn: () => clerkOpsService.getClientActivity(clientId!),
    enabled: !!clientId,
    retry: false,
  });
}

export function useClerkCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ['clerk-customer', customerId],
    queryFn: () => clerkOpsService.getCustomerBusiness(customerId!),
    enabled: !!customerId,
    retry: false, // 404 (not a Clerk store) => not applicable, handled by caller
  });
}

export function useBillingSummary() {
  return useQuery({
    queryKey: ['clerk-ops-billing-summary'],
    queryFn: () => clerkOpsService.getBillingSummary(),
    refetchInterval: 60_000,
    retry: false, // endpoint may not exist until Clerk ships the billing surface
  });
}

export function usePayments() {
  return useQuery({
    queryKey: ['clerk-ops-payments'],
    queryFn: () => clerkOpsService.getPayments(),
    retry: false,
  });
}

export function useAtRisk() {
  return useQuery({
    queryKey: ['clerk-ops-at-risk'],
    queryFn: () => clerkOpsService.getAtRisk(),
    retry: false,
  });
}

/** Activation funnel: signup -> statement (24h) -> P&L (48h) -> subscribed (7d). */
export function useJourney(stalledOnly = false) {
  return useQuery({
    queryKey: ['clerk-ops-journey', stalledOnly],
    queryFn: () => clerkOpsService.getJourney(stalledOnly),
    retry: false,
  });
}
